/**
 * Internal document → published document. One of the two crossings.
 *
 * Built key by key rather than spread, for the same reason `state/normalize.ts`
 * is: a spread publishes whatever the internal type happens to carry today. On a
 * contract other applications parse, that turns an internal refactor into an
 * unreviewed API change — and the first anyone hears of it is a consumer
 * breaking. Listing the keys means adding one to `ProductCard` does *not*
 * silently add it to the wire shape; you have to come here and decide.
 *
 * Optional keys are omitted rather than written as `undefined`. `JSON.stringify`
 * drops `undefined` anyway, so writing them would produce a document whose
 * in-memory form and serialised form differ — which is exactly the kind of
 * difference that makes a round-trip test pass while the file on disk is wrong.
 */

import { IMAGE_FIELD_ID, getDefaultFieldConfiguration } from '../constants';
import { FieldConfiguration, ProductCard } from '../types';
import {
  PRODUCT_CARD_COLLECTION_KIND,
  PRODUCT_CARD_SCHEMA_ID,
  PRODUCT_CARD_SCHEMA_VERSION,
} from './version';
import {
  ProductCardCollection,
  WireCardComponent,
  WireFieldConfiguration,
  WireProductCard,
} from './types';

const encodeConfiguration = (configuration: FieldConfiguration): WireFieldConfiguration => {
  const wire: WireFieldConfiguration = {
    displayLabel: configuration.displayLabel,
    alignment: configuration.alignment,
  };
  if (configuration.imageFit !== undefined) {
    wire.imageFit = configuration.imageFit;
  }
  if (configuration.customCss !== undefined) {
    wire.customCss = configuration.customCss;
  }
  if (configuration.descStyle !== undefined) {
    wire.descStyle = configuration.descStyle;
  }
  if (configuration.strikethrough !== undefined) {
    wire.strikethrough = configuration.strikethrough;
  }
  if (configuration.showUom !== undefined) {
    wire.showUom = configuration.showUom;
  }
  if (configuration.cta !== undefined) {
    // A fresh object: handing out the live one would let a consumer mutate the
    // editor's state through the document it was given.
    wire.cta = { ...configuration.cta };
  }
  return wire;
};

/** One card, as published. */
export const encodeCard = (card: ProductCard): WireProductCard => {
  // O(1) lookups for the single pass below — the only join in the whole encode,
  // and it happens HERE so no consumer ever performs one.
  const placementByField = new Map(card.placements.map((p) => [p.fieldId, p]));
  const enabled = new Set(card.enabledFieldIds);

  const components: WireCardComponent[] = [];
  // Walked in fieldOrder rather than enabledFieldIds order, so the serialised
  // sequence is the card's own order and two identical cards produce
  // byte-identical JSON. That is what makes a content hash — or a plain `===`
  // on the string — a usable "has this changed?" test downstream.
  card.fieldOrder.forEach((fieldId) => {
    if (!enabled.has(fieldId)) {
      return;
    }
    const component: WireCardComponent = {
      fieldId,
      // Every component ships its configuration — the fallback only fires for a
      // pre-seeding internal document (older browser-storage data); the editor
      // itself guarantees an entry for every enabled field.
      configuration: encodeConfiguration(
        card.configuration[fieldId] ?? getDefaultFieldConfiguration(fieldId)
      ),
    };
    const placement = placementByField.get(fieldId);
    if (placement) {
      component.region = placement.region;
      component.order = placement.order;
      if (placement.rowKey !== undefined) {
        component.rowKey = placement.rowKey;
      }
      if (placement.anchor !== undefined) {
        component.anchor = placement.anchor;
      }
    } else if (fieldId === IMAGE_FIELD_ID) {
      // The image has no internal placement (it IS the photo region), but on
      // the wire it says where it renders like every other component.
      component.region = 'photo';
      component.order = 0;
    }
    // A component with no region (unit of measure) contributes no block; its
    // whole effect is the price suffix, carried by its presence + configuration.
    components.push(component);
  });

  const wire: WireProductCard = {
    schemaVersion: PRODUCT_CARD_SCHEMA_VERSION,
    id: card.id,
    name: card.name,
    orientation: card.orientation,
    footerEnabled: card.footerEnabled,
    fieldOrder: [...card.fieldOrder],
    components,
    references: [...card.references],
    updatedAt: card.updatedAt,
  };

  return wire;
};

/**
 * A whole collection, as published.
 *
 * `generatedAt` is a parameter rather than a `new Date()` so this stays pure and
 * a test can assert on the exact bytes. The caller owns the clock — the same
 * split the repository adapter already uses.
 */
export const encodeCollection = (
  cards: ProductCard[],
  generatedAt: string
): ProductCardCollection => ({
  $schema: PRODUCT_CARD_SCHEMA_ID,
  schemaVersion: PRODUCT_CARD_SCHEMA_VERSION,
  kind: PRODUCT_CARD_COLLECTION_KIND,
  generatedAt,
  cards: cards.map(encodeCard),
});
